import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import fonts from '../../design-system/fonts';
import colors from '../../design-system/colors';

export const Section = styled(motion.section)`
  padding: 46px 0px;
  background: ${colors.deepCharcoal};
`;

export const Container = styled.div`
  padding-left: 40px;
  padding-right: 40px;
  box-sizing: border-box;
`;

export const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

export const ContentSection = styled.div`
  margin-bottom: 40px;
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;

  @media (max-width: 768px) {
    justify-content: flex-start;
    margin-bottom: 30px;
  }
`;

export const Subtitle = styled(motion.h2)`
  font-size: 14px;
  font-weight: 700;
  color: ${colors.porcelain};
  opacity: 0.8;
  text-transform: uppercase;
  letter-spacing: 2px;
  line-height: 1.1;
  font-family: ${fonts.headline};
  margin-bottom: 10px;
`;

export const ViewAll = styled(motion(Link))`
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: ${colors.porcelain};
  text-transform: uppercase;
  letter-spacing: -0.02em;
  line-height: 1.1;
  text-decoration: none;
  font-family: ${fonts.headline};
  transition: color 0.3s ease;

  &:hover {
    color: ${colors.sapphire};
    text-decoration: underline;
  }
  @media (max-width: 480px) {
    font-size: 18px;
  }
`;

export const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
`;

export const Title = styled(motion.div)`
  font-size: 45px;
  font-weight: 900;
  color: ${colors.porcelain};
  margin: 0;
  letter-spacing: -0.03em;
  text-transform: uppercase;
  font-family: ${fonts.headline};
  line-height: 30px;

  @media (max-width: 1024px) {
    font-size: 28px;
  }

  @media (max-width: 768px) {
    font-size: 24px;
  }

  @media (max-width: 480px) {
    font-size: 22px;
  }
`;

export const Description = styled(motion.p)`
  font-size: 16px;
  color: gray;
  opacity: 0.8;
  line-height: 1.6;
  margin: 0;
  max-width: 500px;
  text-align: right;
  font-family: ${fonts.body};

  @media (max-width: 768px) {
    text-align: left;
    max-width: 100%;
    font-size: 15px;
  }
`;

export const Grid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 32px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

export const CategoryCard = styled(motion(Link))`
  cursor: pointer;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: visible;
  text-decoration: none;
  color: inherit;
  background: transparent;
  transition: all 0.4s cubic-bezier(0.25, 0.1, 0.25, 1);

  &:hover {
    transform: translateY(-8px);
  }
`;

export const CategoryImageWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  overflow: hidden;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
`;

export const CategoryImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.6s cubic-bezier(0.25, 0.1, 0.25, 1), opacity 0.3s ease;

  ${CategoryCard}:hover & {
    transform: scale(1.1);
  }
`;

export const CategoryPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    135deg,
    ${colors.sapphire} 0%,
    ${colors.frenchBlue} 100%
  );
  color: ${colors.porcelain};
  font-size: 48px;
  font-weight: 900;
  font-family: ${fonts.headline};
  text-transform: uppercase;
  letter-spacing: 2px;

  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

export const CategoryOverlay = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    to bottom,
    rgba(17, 24, 39, 0.6) 0%,
    rgba(17, 24, 39, 0.3) 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;

  ${CategoryCard}:hover & {
    opacity: 1;
  }
`;

export const CategoryArrow = styled(motion.span)`
  font-size: 36px;
  color: #ffffff;
  font-weight: 300;
  transform: translateX(-20px);
  transition: transform 0.3s ease;

  ${CategoryCard}:hover & {
    transform: translateX(0);
  }
`;

export const CategoryName = styled.div`
  font-size: 20px;
  color: gray;
  text-transform: uppercase;
  letter-spacing: 1px;
  padding: 24px 0px;
  text-align: left;
  background: transparent;
  transition: color 0.3s ease;
  font-family: ${fonts.body};
  position: relative;
  z-index: 1;

  ${CategoryCard}:hover & {
    color: ${colors.sapphire};
  }

  @media (max-width: 1024px) {
    font-size: 18px;
  }

  @media (max-width: 768px) {
    padding: 20px 0px;
    font-size: 16px;
  }
`;
